import { GoogleGenerativeAI, SchemaType } from '@google/generative-ai';
import { PaperData } from '../types/paper';

export const ENV_KEY = import.meta.env?.VITE_GEMINI_API_KEY || '';
export const DEFAULT_GEMINI_KEY = ENV_KEY || 'AQ.Ab8RN6LFeTPXgHsM6cLjwwMhiPhSSvdqrygkZIwv6TfpJjaA9Q';
export const DEFAULT_GEMINI_MODEL = 'gemini-flash-latest';

// Clean legacy unsupported model names from localStorage
const purgeLegacyLocalStorage = () => {
  const stored = localStorage.getItem('gemini_model_name');
  if (stored && (stored.includes('1.5') || stored.includes('2.0'))) {
    localStorage.setItem('gemini_model_name', DEFAULT_GEMINI_MODEL);
  }
};
purgeLegacyLocalStorage();

export const getGeminiApiKey = (): string => {
  return localStorage.getItem('gemini_api_key') || DEFAULT_GEMINI_KEY;
};

export const saveGeminiApiKey = (key: string): void => {
  localStorage.setItem('gemini_api_key', key.trim());
};

export const getGeminiModel = (): string => {
  return localStorage.getItem('gemini_model_name') || DEFAULT_GEMINI_MODEL;
};

export const saveGeminiModel = (model: string): void => {
  localStorage.setItem('gemini_model_name', model.trim());
};

const SYSTEM_PROMPT = `You are a world-class AI researcher, domain expert, computer scientist, and educator specializing in deep technical knowledge extraction, mathematical derivation, and spaced-repetition flashcard creation across ANY topic or scientific paper.

Your task is to analyze the user's input (which can be a scientific paper, arXiv link, or freeform topic/domain like "Quantum Key Distribution & Shor Algorithm" or "Distributed Consensus") and produce a comprehensive Knowledge Wiki, Reference Lineage, and High-Yield Flashcard dataset.

CRITICAL QUALITY REQUIREMENTS:
1. ZERO AI SLOP: No generic statements like "this topic is important". Every concept must include exact formulas, quantitative metrics, or concrete mechanisms.
2. MATHEMATICAL RIGOR: Include explicit LaTeX equations for all core equations, algorithms, proofs, or mathematical formulations.
3. REFERENCE LINEAGE: Identify foundational predecessor works/ideas (ancestors) that directly inspired this topic, as well as downstream descendant concepts/fields.
4. HIGH-YIELD FLASHCARDS: Create deep, challenging flashcards testing math derivations, dimensional tracking, computational complexities, edge cases, and tradeoffs.
`;

const PAPER_RESPONSE_SCHEMA = {
  type: SchemaType.OBJECT,
  properties: {
    title: { type: SchemaType.STRING },
    authors: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } },
    year: { type: SchemaType.STRING },
    abstract: { type: SchemaType.STRING },
    lineageGraph: {
      type: SchemaType.OBJECT,
      properties: {
        nodes: {
          type: SchemaType.ARRAY,
          items: {
            type: SchemaType.OBJECT,
            properties: {
              id: { type: SchemaType.STRING },
              label: { type: SchemaType.STRING },
              type: { type: SchemaType.STRING, description: "Must be one of: 'ancestor', 'target_paper', 'descendant'" },
              year: { type: SchemaType.STRING },
              authors: { type: SchemaType.STRING },
              description: { type: SchemaType.STRING },
              keyInnovation: { type: SchemaType.STRING }
            },
            required: ['id', 'label', 'type', 'year', 'description', 'keyInnovation']
          }
        },
        links: {
          type: SchemaType.ARRAY,
          items: {
            type: SchemaType.OBJECT,
            properties: {
              source: { type: SchemaType.STRING },
              target: { type: SchemaType.STRING },
              label: { type: SchemaType.STRING }
            },
            required: ['source', 'target', 'label']
          }
        }
      },
      required: ['nodes', 'links']
    },
    concepts: {
      type: SchemaType.ARRAY,
      items: {
        type: SchemaType.OBJECT,
        properties: {
          id: { type: SchemaType.STRING },
          title: { type: SchemaType.STRING },
          category: { type: SchemaType.STRING, description: "Must be 'core_innovation', 'architectural_component', 'mathematical_formulation', or 'training_technique'" },
          definition: { type: SchemaType.STRING },
          mathFormulas: {
            type: SchemaType.ARRAY,
            items: {
              type: SchemaType.OBJECT,
              properties: {
                label: { type: SchemaType.STRING },
                latex: { type: SchemaType.STRING },
                explanation: { type: SchemaType.STRING }
              },
              required: ['label', 'latex', 'explanation']
            }
          },
          intuition: { type: SchemaType.STRING },
          keyNuances: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } },
          codeSnippet: {
            type: SchemaType.OBJECT,
            properties: {
              language: { type: SchemaType.STRING },
              code: { type: SchemaType.STRING },
              explanation: { type: SchemaType.STRING }
            }
          },
          ancestors: {
            type: SchemaType.ARRAY,
            items: {
              type: SchemaType.OBJECT,
              properties: {
                paperTitle: { type: SchemaType.STRING },
                authorsYear: { type: SchemaType.STRING },
                concept: { type: SchemaType.STRING },
                relationship: { type: SchemaType.STRING }
              },
              required: ['paperTitle', 'authorsYear', 'concept', 'relationship']
            }
          },
          descendants: {
            type: SchemaType.ARRAY,
            items: {
              type: SchemaType.OBJECT,
              properties: {
                concept: { type: SchemaType.STRING },
                paperOrField: { type: SchemaType.STRING },
                impact: { type: SchemaType.STRING }
              },
              required: ['concept', 'paperOrField', 'impact']
            }
          },
          citations: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } }
        },
        required: ['id', 'title', 'category', 'definition', 'mathFormulas', 'intuition', 'keyNuances', 'ancestors', 'descendants', 'citations']
      }
    },
    flashcards: {
      type: SchemaType.ARRAY,
      items: {
        type: SchemaType.OBJECT,
        properties: {
          id: { type: SchemaType.STRING },
          conceptId: { type: SchemaType.STRING },
          question: { type: SchemaType.STRING },
          answer: { type: SchemaType.STRING },
          formula: { type: SchemaType.STRING },
          codeSnippet: { type: SchemaType.STRING },
          figureUrl: { type: SchemaType.STRING },
          figureCaption: { type: SchemaType.STRING },
          tags: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } },
          difficulty: { type: SchemaType.STRING, description: "'easy', 'medium', 'hard', or 'extreme'" }
        },
        required: ['id', 'conceptId', 'question', 'answer', 'tags', 'difficulty']
      }
    }
  },
  required: ['title', 'authors', 'year', 'abstract', 'lineageGraph', 'concepts', 'flashcards']
};

export async function processPaperWithGemini(
  contentPrompt: string,
  pdfBase64?: string,
  apiKey: string = getGeminiApiKey(),
  modelName: string = getGeminiModel()
): Promise<PaperData> {
  const genAI = new GoogleGenerativeAI(apiKey);

  const fallbackModels = [modelName, 'gemini-flash-latest', 'gemini-pro-latest', 'gemini-3.6-flash'];
  const uniqueModels = Array.from(new Set(fallbackModels));

  let lastError: any = null;

  for (const targetModel of uniqueModels) {
    try {
      console.log(`Executing Gemini API call using model: ${targetModel}`);
      const model = genAI.getGenerativeModel({
        model: targetModel,
        systemInstruction: SYSTEM_PROMPT,
        generationConfig: {
          responseMimeType: 'application/json',
          responseSchema: PAPER_RESPONSE_SCHEMA,
          temperature: 0.2,
        }
      });

      const parts: any[] = [];
      if (pdfBase64) {
        parts.push({
          inlineData: {
            data: pdfBase64,
            mimeType: 'application/pdf'
          }
        });
      }

      parts.push({
        text: `Analyze the following input topic / paper context and generate the full Knowledge Wiki, Lineage Graph, and High-Yield Flashcards.\n\nInput Prompt:\n${contentPrompt}`
      });

      const result = await model.generateContent(parts);
      const responseText = result.response.text();
      
      const rawData = JSON.parse(responseText);
      return {
        id: `topic-${Date.now()}`,
        title: rawData.title || 'Extracted Knowledge Topic',
        authors: rawData.authors || ['Community / Literature'],
        year: rawData.year || new Date().getFullYear().toString(),
        abstract: rawData.abstract || '',
        lineageGraph: rawData.lineageGraph || { nodes: [], links: [] },
        concepts: rawData.concepts || [],
        flashcards: rawData.flashcards || []
      };
    } catch (err: any) {
      console.warn(`Model ${targetModel} attempt failed:`, err);
      lastError = err;
      // Brief pause before trying fallback model if rate-limited or busy
      await new Promise(res => setTimeout(res, 500));
    }
  }

  throw new Error(`Gemini API call failed across models (${uniqueModels.join(', ')}). Error: ${lastError?.message || 'Check your API key and permissions.'}`);
}
